import { ChangeEvent, FormEvent, PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Archive, Copy, Image as ImageIcon, Loader2, Pencil, Plus, RefreshCcw, Save, Trash2, Upload, Wand2 } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  archiveBadge,
  Badge,
  BadgePayload,
  BadgeTextArea,
  createBadge,
  deleteBadge,
  getBadges,
  updateBadge,
  uploadBadgeImage
} from '../lib/api/badges'
import { cn } from '../utils/cn'

type BadgeFormState = Omit<BadgePayload, 'layoutAreas'> & {
  layoutAreas: BadgeTextArea[]
}

const CATEGORY_OPTIONS = ['seasonal', 'hikestrong', 'achievement'] as const

const createTextArea = (label = 'Title'): BadgeTextArea => ({
  id: createClientId(),
  label,
  text: label === 'Title' ? 'SUMMIT STREAK' : '',
  x: 18,
  y: label === 'Title' ? 62 : 78,
  width: 64,
  height: label === 'Title' ? 16 : 10,
  fontFamily: 'Inter, Arial, sans-serif',
  fontSize: label === 'Title' ? 54 : 30,
  fontWeight: label === 'Title' ? '800' : '700',
  color: '#ffffff',
  textAlign: 'center',
  verticalAlign: 'middle',
  maxLines: label === 'Title' ? 2 : 1,
  curve: 0
})

const AREA_NUMERIC_LIMITS = {
  x: [0, 100],
  y: [0, 100],
  width: [0, 100],
  height: [0, 100],
  fontSize: [8, 160],
  maxLines: [1, 4],
  curve: [-100, 100]
} as const satisfies Partial<Record<keyof BadgeTextArea, readonly [number, number]>>

// Percent of the preview's height that maps a curve-handle pull to the full ±100 range.
const CURVE_DRAG_RANGE_PCT = 18
const MIN_ZONE_SIZE_PCT = 4

type ZoneDragMode = 'move' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se' | 'curve'

type ZoneDragState = {
  areaId: string
  mode: ZoneDragMode
  startXPct: number
  startYPct: number
  orig: { x: number; y: number; width: number; height: number; curve: number }
}

const roundPct = (value: number) => Math.round(value * 10) / 10

// Native color inputs only accept #rrggbb; map whatever is in the hex field to that.
const toPickerHex = (value: string) => {
  const trimmed = value.trim().toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
  }
  return '#ffffff'
}

const clampAreaPatch = (patch: Partial<BadgeTextArea>): Partial<BadgeTextArea> => {
  const clamped: Partial<BadgeTextArea> = { ...patch }
  for (const [field, [min, max]] of Object.entries(AREA_NUMERIC_LIMITS)) {
    const value = clamped[field as keyof BadgeTextArea]
    if (typeof value === 'number') {
      const next = Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min
      ;(clamped as Record<string, unknown>)[field] = next
    }
  }
  return clamped
}

const initialForm: BadgeFormState = {
  slug: null,
  name: '',
  category: 'seasonal',
  description: null,
  baseImageUrl: null,
  renderedImageUrl: null,
  layoutAreas: [createTextArea()],
  isArchived: false
}

function createClientId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `area-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const isBadgeImageFile = (file: File) => {
  return ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)
}

const normalizeBadgeKey = (form: BadgeFormState) => form.slug?.trim() || form.name.trim() || 'badge'

const canvasImageCache = new Map<string, Promise<HTMLImageElement>>()

const loadCanvasImage = (source: string) => {
  const cached = canvasImageCache.get(source)
  if (cached) return cached

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Could not load badge image.'))
    image.src = source
  })

  canvasImageCache.set(source, promise)
  promise.catch(() => canvasImageCache.delete(source))
  return promise
}

const wrapText = (
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
) => {
  const words = text.trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []

  for (const word of words) {
    const current = lines[lines.length - 1] ?? ''
    const candidate = current ? `${current} ${word}` : word
    if (context.measureText(candidate).width <= maxWidth || !current) {
      if (lines.length === 0) {
        lines.push(candidate)
      } else {
        lines[lines.length - 1] = candidate
      }
      continue
    }

    if (lines.length >= maxLines) {
      lines[lines.length - 1] = `${current}...`
      return lines
    }

    lines.push(word)
  }

  return lines.slice(0, maxLines)
}

const curvedTextMetrics = (
  context: CanvasRenderingContext2D,
  text: string,
  curve: number,
  fontSize: number
) => {
  const arcAngle = (Math.abs(curve) / 100) * Math.PI
  const textWidth = context.measureText(text).width
  const radius = Math.max(textWidth / arcAngle, fontSize * 0.75)
  const span = textWidth / radius
  const chord = 2 * radius * Math.sin(span / 2)
  const sagitta = radius * (1 - Math.cos(span / 2))
  return { radius, chord, sagitta, blockHeight: sagitta + fontSize * 1.08, textWidth }
}

const drawCurvedText = (
  context: CanvasRenderingContext2D,
  text: string,
  rect: { x: number; y: number; width: number; height: number },
  curve: number,
  fontSize: number,
  verticalAlign: BadgeTextArea['verticalAlign']
) => {
  const { radius, sagitta, blockHeight, textWidth } = curvedTextMetrics(context, text, curve, fontSize)
  const archesUp = curve > 0
  const centerX = rect.x + rect.width / 2
  const blockTop = verticalAlign === 'top'
    ? rect.y
    : verticalAlign === 'bottom'
      ? rect.y + rect.height - blockHeight
      : rect.y + (rect.height - blockHeight) / 2
  // baseline of the arc apex; ends bow away from it by the sagitta
  const apexY = archesUp
    ? blockTop + fontSize * 0.8
    : blockTop + sagitta + fontSize * 0.8
  const centerY = archesUp ? apexY + radius : apexY - radius

  let advance = -textWidth / 2
  for (const char of text) {
    const charWidth = context.measureText(char).width
    const angle = (advance + charWidth / 2) / radius
    advance += charWidth
    context.save()
    if (archesUp) {
      context.translate(centerX + Math.sin(angle) * radius, centerY - Math.cos(angle) * radius)
      context.rotate(angle)
    } else {
      context.translate(centerX + Math.sin(angle) * radius, centerY + Math.cos(angle) * radius)
      context.rotate(-angle)
    }
    context.fillText(char, 0, 0)
    context.restore()
  }
}

const renderBadgeBlob = async (source: string, areas: BadgeTextArea[], options?: { maxSize?: number }) => {
  const image = await loadCanvasImage(source)
  const canvas = document.createElement('canvas')
  const size = Math.min(Math.max(image.naturalWidth || 1024, 512), options?.maxSize ?? 2048)
  canvas.width = size
  canvas.height = size

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Canvas rendering is unavailable.')
  }

  context.clearRect(0, 0, size, size)
  context.drawImage(image, 0, 0, size, size)

  for (const area of areas) {
    const text = area.text.trim()
    if (!text) continue

    const rect = {
      x: (area.x / 100) * size,
      y: (area.y / 100) * size,
      width: (area.width / 100) * size,
      height: (area.height / 100) * size
    }
    const curve = Math.max(-100, Math.min(100, Math.round(area.curve ?? 0)))
    let fontSize = Math.max(8, Math.round(area.fontSize * (size / 1024)))

    if (curve !== 0) {
      // curved text renders as a single line bent along a circular arc
      while (fontSize >= 8) {
        context.font = `${area.fontWeight} ${fontSize}px ${area.fontFamily}`
        const { chord, blockHeight } = curvedTextMetrics(context, text, curve, fontSize)
        if (chord <= rect.width && blockHeight <= rect.height) {
          break
        }
        fontSize -= 2
      }

      context.font = `${area.fontWeight} ${fontSize}px ${area.fontFamily}`
      context.fillStyle = area.color
      context.textAlign = 'center'
      context.textBaseline = 'alphabetic'
      context.shadowColor = 'rgba(0, 0, 0, 0.35)'
      context.shadowBlur = Math.max(2, Math.round(fontSize * 0.08))
      context.shadowOffsetY = Math.max(1, Math.round(fontSize * 0.04))

      drawCurvedText(context, text, rect, curve, fontSize, area.verticalAlign)
      context.shadowColor = 'transparent'
      continue
    }

    let lines: string[] = []

    while (fontSize >= 8) {
      context.font = `${area.fontWeight} ${fontSize}px ${area.fontFamily}`
      lines = wrapText(context, text, rect.width, area.maxLines)
      const lineHeight = fontSize * 1.08
      const widest = Math.max(...lines.map((line) => context.measureText(line).width), 0)
      if (widest <= rect.width && lines.length * lineHeight <= rect.height) {
        break
      }
      fontSize -= 2
    }

    const lineHeight = fontSize * 1.08
    const blockHeight = lines.length * lineHeight
    const startY = area.verticalAlign === 'top'
      ? rect.y + lineHeight * 0.8
      : area.verticalAlign === 'bottom'
        ? rect.y + rect.height - blockHeight + lineHeight * 0.8
        : rect.y + (rect.height - blockHeight) / 2 + lineHeight * 0.8
    const x = area.textAlign === 'left'
      ? rect.x
      : area.textAlign === 'right'
        ? rect.x + rect.width
        : rect.x + rect.width / 2

    context.font = `${area.fontWeight} ${fontSize}px ${area.fontFamily}`
    context.fillStyle = area.color
    context.textAlign = area.textAlign
    context.textBaseline = 'alphabetic'
    context.shadowColor = 'rgba(0, 0, 0, 0.35)'
    context.shadowBlur = Math.max(2, Math.round(fontSize * 0.08))
    context.shadowOffsetY = Math.max(1, Math.round(fontSize * 0.04))

    lines.forEach((line, index) => {
      context.fillText(line, x, startY + index * lineHeight)
    })

    context.shadowColor = 'transparent'
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('Could not render badge image.'))
      }
    }, 'image/png')
  })
}

export const BadgesAdminPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [badges, setBadges] = useState<Badge[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [rendering, setRendering] = useState(false)
  const [uploadingBase, setUploadingBase] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<BadgeFormState>(initialForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [basePreviewUrl, setBasePreviewUrl] = useState<string | null>(null)
  const [renderedPreviewUrl, setRenderedPreviewUrl] = useState<string | null>(null)
  const [selectedAreaId, setSelectedAreaId] = useState(initialForm.layoutAreas[0].id)
  const [livePreviewUrl, setLivePreviewUrl] = useState<string | null>(null)
  const baseInputRef = useRef<HTMLInputElement>(null)
  const previewBoxRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<ZoneDragState | null>(null)

  const role = (user?.role ?? 'Guest').toString()
  const isEditor = role === 'Admin' || role === 'Editor'

  const loadBadges = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getBadges(showArchived)
      setBadges(data)
    } catch (error) {
      console.error('Failed to load badges', error)
      addToast({ title: 'Error', description: 'Could not load badges.' })
    } finally {
      setLoading(false)
    }
  }, [addToast, showArchived])

  useEffect(() => {
    if (!isEditor) {
      setLoading(false)
      return
    }

    void loadBadges()
  }, [isEditor, loadBadges])

  useEffect(() => {
    return () => {
      if (basePreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(basePreviewUrl)
      if (renderedPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(renderedPreviewUrl)
      if (livePreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(livePreviewUrl)
    }
  }, [basePreviewUrl, renderedPreviewUrl, livePreviewUrl])

  const selectedArea = useMemo(
    () => form.layoutAreas.find((area) => area.id === selectedAreaId) ?? form.layoutAreas[0],
    [form.layoutAreas, selectedAreaId]
  )

  const previewSource = livePreviewUrl || renderedPreviewUrl || form.renderedImageUrl || basePreviewUrl || form.baseImageUrl
  const baseRenderSource = basePreviewUrl || form.baseImageUrl || null

  // Debounced live render so the preview shows the real text (including curves) while dragging.
  useEffect(() => {
    if (!baseRenderSource) {
      setLivePreviewUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous)
        return null
      })
      return
    }

    let cancelled = false
    const timer = window.setTimeout(() => {
      renderBadgeBlob(baseRenderSource, form.layoutAreas, { maxSize: 768 })
        .then((blob) => {
          if (cancelled) return
          const url = URL.createObjectURL(blob)
          setLivePreviewUrl((previous) => {
            if (previous) URL.revokeObjectURL(previous)
            return url
          })
        })
        .catch(() => {
          // Base image not loadable (yet) — keep whatever the preview currently shows.
        })
    }, 150)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [baseRenderSource, form.layoutAreas])

  const pointerToPct = (event: ReactPointerEvent) => {
    const rect = previewBoxRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return null
    return {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100
    }
  }

  const beginZoneDrag = (event: ReactPointerEvent, area: BadgeTextArea, mode: ZoneDragMode) => {
    event.preventDefault()
    event.stopPropagation()
    const point = pointerToPct(event)
    if (!point) return
    setSelectedAreaId(area.id)
    dragRef.current = {
      areaId: area.id,
      mode,
      startXPct: point.x,
      startYPct: point.y,
      orig: { x: area.x, y: area.y, width: area.width, height: area.height, curve: area.curve ?? 0 }
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onZoneDragMove = (event: ReactPointerEvent) => {
    const drag = dragRef.current
    if (!drag) return
    const point = pointerToPct(event)
    if (!point) return
    const dx = point.x - drag.startXPct
    const dy = point.y - drag.startYPct
    const orig = drag.orig

    if (drag.mode === 'move') {
      updateArea(drag.areaId, {
        x: roundPct(Math.min(Math.max(orig.x + dx, 0), 100 - orig.width)),
        y: roundPct(Math.min(Math.max(orig.y + dy, 0), 100 - orig.height))
      })
      return
    }

    if (drag.mode === 'curve') {
      const curve = Math.round(orig.curve - dy * (100 / CURVE_DRAG_RANGE_PCT))
      updateArea(drag.areaId, { curve: Math.min(100, Math.max(-100, curve)) })
      return
    }

    const corner = drag.mode.slice('resize-'.length)
    let { x, y, width, height } = orig
    if (corner.includes('w')) {
      x = Math.min(Math.max(orig.x + dx, 0), orig.x + orig.width - MIN_ZONE_SIZE_PCT)
      width = orig.width + (orig.x - x)
    }
    if (corner.includes('e')) {
      width = Math.min(Math.max(orig.width + dx, MIN_ZONE_SIZE_PCT), 100 - orig.x)
    }
    if (corner.includes('n')) {
      y = Math.min(Math.max(orig.y + dy, 0), orig.y + orig.height - MIN_ZONE_SIZE_PCT)
      height = orig.height + (orig.y - y)
    }
    if (corner.includes('s')) {
      height = Math.min(Math.max(orig.height + dy, MIN_ZONE_SIZE_PCT), 100 - orig.y)
    }
    updateArea(drag.areaId, {
      x: roundPct(x),
      y: roundPct(y),
      width: roundPct(width),
      height: roundPct(height)
    })
  }

  const endZoneDrag = (event: ReactPointerEvent) => {
    if (!dragRef.current) return
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const resetForm = useCallback(() => {
    const titleArea = createTextArea()
    setEditingId(null)
    setForm({
      ...initialForm,
      layoutAreas: [titleArea]
    })
    setFormError(null)
    setBasePreviewUrl(null)
    setRenderedPreviewUrl(null)
    setSelectedAreaId(titleArea.id)
  }, [])

  const editBadge = (badge: Badge) => {
    const fallbackArea = createTextArea()
    const layoutAreas = badge.layoutAreas.length > 0 ? badge.layoutAreas : [fallbackArea]
    setEditingId(badge.id)
    setForm({
      slug: badge.slug,
      name: badge.name,
      category: badge.category,
      description: badge.description ?? null,
      baseImageUrl: badge.baseImageUrl ?? null,
      renderedImageUrl: badge.renderedImageUrl ?? null,
      layoutAreas,
      isArchived: badge.isArchived
    })
    setBasePreviewUrl(null)
    setRenderedPreviewUrl(null)
    setSelectedAreaId(layoutAreas[0].id)
    setFormError(null)
  }

  const updateArea = (id: string, patch: Partial<BadgeTextArea>) => {
    const clamped = clampAreaPatch(patch)
    setForm((prev) => ({
      ...prev,
      renderedImageUrl: null,
      layoutAreas: prev.layoutAreas.map((area) => area.id === id ? { ...area, ...clamped } : area)
    }))
    setRenderedPreviewUrl(null)
  }

  const addArea = () => {
    const next = createTextArea(form.layoutAreas.length === 0 ? 'Title' : 'Subtitle')
    setForm((prev) => ({
      ...prev,
      renderedImageUrl: null,
      layoutAreas: [...prev.layoutAreas, next]
    }))
    setSelectedAreaId(next.id)
  }

  const removeArea = (id: string) => {
    setForm((prev) => {
      const nextAreas = prev.layoutAreas.filter((area) => area.id !== id)
      return {
        ...prev,
        renderedImageUrl: null,
        layoutAreas: nextAreas.length > 0 ? nextAreas : [createTextArea()]
      }
    })
    setRenderedPreviewUrl(null)
  }

  const handleBaseUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!isBadgeImageFile(file)) {
      addToast({ title: 'Invalid image', description: 'Upload a PNG, JPG, or WEBP badge image.' })
      return
    }

    const objectUrl = URL.createObjectURL(file)
    setBasePreviewUrl((previous) => {
      if (previous?.startsWith('blob:')) URL.revokeObjectURL(previous)
      return objectUrl
    })
    setRenderedPreviewUrl(null)
    setUploadingBase(true)
    try {
      const upload = await uploadBadgeImage(file, normalizeBadgeKey(form), 'base')
      setForm((prev) => ({
        ...prev,
        baseImageUrl: upload.imageUrl,
        renderedImageUrl: null
      }))
      addToast({ title: 'Base image uploaded', description: 'Badge base art is ready.' })
    } catch (error) {
      console.error('Badge base image upload failed', error)
      addToast({ title: 'Upload failed', description: 'Could not upload the base image.' })
    } finally {
      setUploadingBase(false)
    }
  }

  const renderAndUpload = async () => {
    if (!baseRenderSource) {
      throw new Error('Upload a base image before rendering.')
    }

    const blob = await renderBadgeBlob(baseRenderSource, form.layoutAreas)
    const localUrl = URL.createObjectURL(blob)
    setRenderedPreviewUrl((previous) => {
      if (previous?.startsWith('blob:')) URL.revokeObjectURL(previous)
      return localUrl
    })

    const file = new File([blob], `${normalizeBadgeKey(form)}.png`, { type: 'image/png' })
    const upload = await uploadBadgeImage(file, normalizeBadgeKey(form), 'rendered')
    setForm((prev) => ({ ...prev, renderedImageUrl: upload.imageUrl }))
    return upload.imageUrl
  }

  const handleRender = async () => {
    setRendering(true)
    setFormError(null)
    try {
      await renderAndUpload()
      addToast({ title: 'Badge rendered', description: 'Final badge image is uploaded.' })
    } catch (error) {
      console.error('Badge render failed', error)
      setFormError(error instanceof Error ? error.message : 'Could not render badge.')
      addToast({ title: 'Render failed', description: 'Could not render and upload the badge.' })
    } finally {
      setRendering(false)
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const name = form.name.trim()
    if (!name) {
      setFormError('Name is required.')
      return
    }

    setSaving(true)
    setFormError(null)
    try {
      let renderedImageUrl = form.renderedImageUrl?.trim() || null
      if (!renderedImageUrl && baseRenderSource) {
        renderedImageUrl = await renderAndUpload()
      }

      const payload: BadgePayload = {
        ...form,
        name,
        slug: form.slug?.trim() || null,
        category: form.category.trim() || 'seasonal',
        description: form.description?.trim() || null,
        baseImageUrl: form.baseImageUrl?.trim() || null,
        renderedImageUrl,
        layoutAreas: form.layoutAreas,
        isArchived: form.isArchived ?? false
      }

      const saved = editingId
        ? await updateBadge(editingId, payload)
        : await createBadge(payload)

      setBadges((prev) => {
        const withoutSaved = prev.filter((badge) => badge.id !== saved.id)
        return [saved, ...withoutSaved]
      })
      addToast({ title: editingId ? 'Badge updated' : 'Badge created', description: 'Badge changes are saved.' })
      editBadge(saved)
    } catch (error) {
      console.error('Failed to save badge', error)
      setFormError(error instanceof Error ? error.message : 'Could not save badge.')
      addToast({ title: 'Save failed', description: 'Could not save the badge.' })
    } finally {
      setSaving(false)
    }
  }

  const handleDuplicate = async (badge: Badge) => {
    const takenNames = new Set(
      badges
        .filter((item) => item.category === badge.category)
        .map((item) => item.name.toLowerCase())
    )
    let name = `${badge.name} (Copy)`
    for (let attempt = 2; takenNames.has(name.toLowerCase()); attempt += 1) {
      name = `${badge.name} (Copy ${attempt})`
    }

    try {
      const created = await createBadge({
        slug: null,
        name,
        category: badge.category,
        description: badge.description ?? null,
        baseImageUrl: badge.baseImageUrl ?? null,
        renderedImageUrl: badge.renderedImageUrl ?? null,
        layoutAreas: badge.layoutAreas.map((area) => ({ ...area, id: createClientId() })),
        isArchived: false
      })
      setBadges((prev) => [created, ...prev])
      addToast({ title: 'Badge duplicated', description: `Created "${created.name}".` })
      editBadge(created)
    } catch (error) {
      console.error('Failed to duplicate badge', error)
      addToast({ title: 'Error', description: 'Could not duplicate the badge.' })
    }
  }

  const handleArchive = async (badge: Badge) => {
    try {
      const updated = await archiveBadge(badge.id, !badge.isArchived)
      setBadges((prev) =>
        showArchived
          ? prev.map((item) => item.id === updated.id ? updated : item)
          : prev.filter((item) => item.id !== updated.id)
      )
      addToast({
        title: updated.isArchived ? 'Badge archived' : 'Badge restored',
        description: `Badge has been ${updated.isArchived ? 'archived' : 'restored'}.`
      })
      if (editingId === badge.id) {
        editBadge(updated)
      }
    } catch (error) {
      console.error('Failed to archive badge', error)
      addToast({ title: 'Error', description: 'Could not update badge archive state.' })
    }
  }

  const handleDelete = async (badge: Badge) => {
    const confirmed = window.confirm(
      `Delete "${badge.name}"? This permanently removes the badge and any of its images not used elsewhere. This cannot be undone.`
    )
    if (!confirmed) return

    try {
      await deleteBadge(badge.id)
      setBadges((prev) => prev.filter((item) => item.id !== badge.id))
      if (editingId === badge.id) {
        resetForm()
      }
      addToast({ title: 'Badge deleted', description: `"${badge.name}" has been removed.` })
    } catch (error) {
      console.error('Failed to delete badge', error)
      addToast({ title: 'Error', description: 'Could not delete the badge.' })
    }
  }

  if (!isEditor) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
        <p className="font-medium text-foreground">Badges can only be managed by Admin or Editor accounts.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Badges</h1>
          <p className="text-sm text-muted-foreground">
            Build reusable badge images for seasonal challenges, HikeStrong routes, and future achievements.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => setShowArchived((value) => !value)}>
            <Archive className="h-4 w-4" />
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
          <Button type="button" variant="outline" onClick={() => void loadBadges()} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button type="button" onClick={resetForm}>
            <Plus className="h-4 w-4" />
            New Badge
          </Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{editingId ? 'Edit Badge' : 'Create Badge'}</CardTitle>
              <CardDescription>Upload base art, place text zones, and save the rendered badge image.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {formError ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {formError}
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium" htmlFor="badge-name">Name</label>
                  <Input
                    id="badge-name"
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="Summer Summit"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="badge-category">Category</label>
                  <select
                    id="badge-category"
                    value={form.category}
                    onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {CATEGORY_OPTIONS.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="badge-slug">Slug</label>
                  <Input
                    id="badge-slug"
                    value={form.slug ?? ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, slug: event.target.value }))}
                    placeholder="summer-summit"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="badge-rendered-url">Rendered Image URL</label>
                  <Input
                    id="badge-rendered-url"
                    value={form.renderedImageUrl ?? ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, renderedImageUrl: event.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="badge-description">Description</label>
                <Textarea
                  id="badge-description"
                  value={form.description ?? ''}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  rows={2}
                />
              </div>

              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">Preview</span>
                    <div className="flex gap-2">
                      <input
                        ref={baseInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={handleBaseUpload}
                      />
                      <Button type="button" variant="outline" onClick={() => baseInputRef.current?.click()} disabled={uploadingBase}>
                        {uploadingBase ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Base Art
                      </Button>
                      <Button type="button" variant="outline" onClick={() => void handleRender()} disabled={rendering || saving || !baseRenderSource}>
                        {rendering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                        Render PNG
                      </Button>
                    </div>
                  </div>

                  <div ref={previewBoxRef} className="relative aspect-square overflow-hidden rounded-lg border border-border bg-muted">
                    {previewSource ? (
                      <>
                        <img
                          src={previewSource}
                          alt="Badge preview"
                          className="h-full w-full object-contain"
                          crossOrigin="anonymous"
                        />
                        {baseRenderSource ? (
                          <div className="absolute inset-0 touch-none select-none">
                            {form.layoutAreas.map((area) => {
                              const isSelected = selectedAreaId === area.id
                              const curve = area.curve ?? 0
                              const centerXPct = area.x + area.width / 2
                              const handleTopPct = area.y - (curve / 100) * CURVE_DRAG_RANGE_PCT
                              return (
                                <div key={area.id}>
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Move ${area.label} zone`}
                                    className={cn(
                                      'absolute cursor-move border outline-none',
                                      isSelected
                                        ? 'border-cyan-200 bg-cyan-300/10 shadow-sm'
                                        : 'border-cyan-400/50 bg-transparent hover:border-cyan-300'
                                    )}
                                    style={{
                                      left: `${area.x}%`,
                                      top: `${area.y}%`,
                                      width: `${area.width}%`,
                                      height: `${area.height}%`
                                    }}
                                    onPointerDown={(event) => beginZoneDrag(event, area, 'move')}
                                    onPointerMove={onZoneDragMove}
                                    onPointerUp={endZoneDrag}
                                    onPointerCancel={endZoneDrag}
                                  >
                                    <span className="absolute -top-0.5 left-0 -translate-y-full rounded-sm bg-cyan-600/90 px-1 py-px text-[9px] font-semibold text-white">
                                      {area.label}
                                    </span>
                                  </div>
                                  {isSelected ? (
                                    <>
                                      {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
                                        <div
                                          key={corner}
                                          aria-label={`Resize ${area.label} zone`}
                                          className={cn(
                                            'absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-white bg-cyan-500 shadow',
                                            corner === 'nw' || corner === 'se' ? 'cursor-nwse-resize' : 'cursor-nesw-resize'
                                          )}
                                          style={{
                                            left: `${corner.includes('w') ? area.x : area.x + area.width}%`,
                                            top: `${corner.includes('n') ? area.y : area.y + area.height}%`
                                          }}
                                          onPointerDown={(event) => beginZoneDrag(event, area, `resize-${corner}`)}
                                          onPointerMove={onZoneDragMove}
                                          onPointerUp={endZoneDrag}
                                          onPointerCancel={endZoneDrag}
                                        />
                                      ))}
                                      <div
                                        className="pointer-events-none absolute w-px bg-amber-300/80"
                                        style={{
                                          left: `${centerXPct}%`,
                                          top: `${Math.min(handleTopPct, area.y)}%`,
                                          height: `${Math.abs(handleTopPct - area.y)}%`
                                        }}
                                      />
                                      <div
                                        aria-label={`Adjust ${area.label} curve`}
                                        title={`Curve: ${curve} (pull up to arch up, down to arch down)`}
                                        className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize rounded-full border-2 border-white bg-amber-500 shadow-md"
                                        style={{ left: `${centerXPct}%`, top: `${handleTopPct}%` }}
                                        onPointerDown={(event) => beginZoneDrag(event, area, 'curve')}
                                        onPointerMove={onZoneDragMove}
                                        onPointerUp={endZoneDrag}
                                        onPointerCancel={endZoneDrag}
                                      />
                                    </>
                                  ) : null}
                                </div>
                              )
                            })}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                        <ImageIcon className="h-10 w-10" />
                        <span className="text-sm">No badge art</span>
                      </div>
                    )}
                  </div>
                  {baseRenderSource ? (
                    <p className="text-xs text-muted-foreground">
                      Drag a box to move it, corner squares to resize, and pull the gold handle to curve the text.
                    </p>
                  ) : null}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Text Zones</span>
                    <Button type="button" variant="outline" size="sm" onClick={addArea}>
                      <Plus className="h-4 w-4" />
                      Add
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {form.layoutAreas.map((area) => (
                      <Button
                        key={area.id}
                        type="button"
                        size="sm"
                        variant={selectedAreaId === area.id ? 'default' : 'outline'}
                        onClick={() => setSelectedAreaId(area.id)}
                      >
                        {area.label}
                      </Button>
                    ))}
                  </div>

                  {selectedArea ? (
                    <div className="space-y-3 rounded-lg border border-border p-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label className="text-xs font-medium" htmlFor="area-label">Label</label>
                          <Input
                            id="area-label"
                            value={selectedArea.label}
                            onChange={(event) => updateArea(selectedArea.id, { label: event.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium" htmlFor="area-color">Color</label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              id="area-color-picker"
                              aria-label="Pick text color"
                              className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
                              value={toPickerHex(selectedArea.color)}
                              onChange={(event) => updateArea(selectedArea.id, { color: event.target.value })}
                            />
                            <Input
                              id="area-color"
                              value={selectedArea.color}
                              onChange={(event) => updateArea(selectedArea.id, { color: event.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium" htmlFor="area-text">Text</label>
                        <Input
                          id="area-text"
                          value={selectedArea.text}
                          onChange={(event) => updateArea(selectedArea.id, { text: event.target.value })}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Position x {selectedArea.x}% · y {selectedArea.y}% · size {selectedArea.width}×{selectedArea.height}% · curve {selectedArea.curve ?? 0}
                        {' — '}adjust by dragging on the preview.
                      </p>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="space-y-1">
                          <label className="text-xs font-medium" htmlFor="area-font-size">Font Size</label>
                          <Input
                            id="area-font-size"
                            type="number"
                            min={8}
                            max={160}
                            value={selectedArea.fontSize}
                            onChange={(event) => updateArea(selectedArea.id, { fontSize: Number(event.target.value) })}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium" htmlFor="area-weight">Weight</label>
                          <Input
                            id="area-weight"
                            value={selectedArea.fontWeight}
                            onChange={(event) => updateArea(selectedArea.id, { fontWeight: event.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium" htmlFor="area-lines">Lines</label>
                          <Input
                            id="area-lines"
                            type="number"
                            min={1}
                            max={4}
                            value={selectedArea.maxLines}
                            onChange={(event) => updateArea(selectedArea.id, { maxLines: Number(event.target.value) })}
                          />
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label className="text-xs font-medium" htmlFor="area-align">Align</label>
                          <select
                            id="area-align"
                            value={selectedArea.textAlign}
                            onChange={(event) => updateArea(selectedArea.id, { textAlign: event.target.value as BadgeTextArea['textAlign'] })}
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          >
                            <option value="left">left</option>
                            <option value="center">center</option>
                            <option value="right">right</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium" htmlFor="area-valign">Vertical</label>
                          <select
                            id="area-valign"
                            value={selectedArea.verticalAlign}
                            onChange={(event) => updateArea(selectedArea.id, { verticalAlign: event.target.value as BadgeTextArea['verticalAlign'] })}
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          >
                            <option value="top">top</option>
                            <option value="middle">middle</option>
                            <option value="bottom">bottom</option>
                          </select>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeArea(selectedArea.id)}
                        disabled={form.layoutAreas.length <= 1}
                      >
                        Remove Zone
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} disabled={saving || rendering}>
                  Reset
                </Button>
                <Button type="submit" disabled={saving || rendering || uploadingBase}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Badge
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        <div className="space-y-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Badge Catalog</h2>
              <p className="text-sm text-muted-foreground">{badges.length} badges</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center rounded-lg border border-border py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : badges.length === 0 ? (
            <EmptyState
              title="No badges yet"
              description="Create a badge from base art and text zones."
              icon={ImageIcon}
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
              {badges.map((badge) => (
                <Card key={badge.id} className={cn(badge.isArchived && 'opacity-70')}>
                  <CardContent className="flex gap-3 p-4">
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                      {badge.renderedImageUrl || badge.baseImageUrl ? (
                        <img
                          src={badge.renderedImageUrl ?? badge.baseImageUrl ?? ''}
                          alt={badge.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <ImageIcon className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="truncate font-semibold text-foreground">{badge.name}</h3>
                          <p className="truncate text-xs text-muted-foreground">{badge.category} / {badge.slug}</p>
                        </div>
                        <span className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-semibold',
                          badge.renderedImageUrl ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        )}>
                          {badge.renderedImageUrl ? 'Rendered' : 'Draft'}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => editBadge(badge)}>
                          <Pencil className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => void handleDuplicate(badge)}>
                          <Copy className="h-4 w-4" />
                          Duplicate
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => void handleArchive(badge)}>
                          <Archive className="h-4 w-4" />
                          {badge.isArchived ? 'Restore' : 'Archive'}
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={() => void handleDelete(badge)}>
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
